/**
 * src/components/HexclaveAuthForm.tsx — the signed-out surface in Hexclave mode.
 *
 * Plan: `docs/PLAN-STACKAUTH.md` §7.1 item 5 (the form), §9 change 5 (the pending-approval screen),
 * §7 (hosted pages own sign-in, sign-up, password reset and email verification).
 *
 * The sibling of `LegacyAuthForm`. `AuthForm` picks between them at build time; `ProtectedRoute` is
 * untouched and still renders `<AuthForm/>` **in place** when `!isAuthenticated`, with the URL
 * unchanged (spec §1.4.2).
 *
 * ═══ WHY THERE ARE NO FIELDS HERE ══════════════════════════════════════════════════════════════
 * There is no email input, no password input and no validation, deliberately. Credentials are
 * Hexclave's business now, and sign-in, sign-up, **password reset** and email verification are the
 * SDK's own maintained pages. Rebuilding those forms here would recreate exactly what this migration
 * exists to stop maintaining — password reset is the entire reason for it (plan §1).
 *
 * They render on **our** origin, at `/handler/*`: `urls.default.type: 'handler-component'`
 * (`src/lib/hexclave.ts`) plus the matching route in `src/App.tsx`. The buttons below therefore
 * navigate within the app rather than handing the user to a vendor domain.
 *
 * ═══ THE THREE STATES, AND WHY THE LAST TWO MUST EXIST ═════════════════════════════════════════
 * `isAuthenticated` is `!!user`, and `user` is the **D1** row from `GET /api/auth/me` — not the
 * Hexclave session. Those come apart for two real, expected classes of visitor:
 *
 *   | Hexclave session   | D1 row     | `isAuthenticated` | What this renders            |
 *   |--------------------|------------|-------------------|------------------------------|
 *   | none               | —          | false             | (A) sign in / create account |
 *   | valid, unverified  | —          | false             | (C) verify your email        |
 *   | valid              | unapproved | false             | (B) waiting for an admin     |
 *   | valid              | approved   | true              | nothing — the app renders    |
 *
 * Without (B) a newly signed-up translator lands on a **sign-in button that appears to do nothing**:
 * they are already signed in to Hexclave, so pressing it bounces off the hosted page and returns
 * them here unchanged, with no explanation. Plan §9 change 5 calls for reproducing today's message
 * instead, and that is what (B) does — the string is the one the Worker already sends
 * (`worker/lib/middleware.ts` `requireAuth`, `worker/routes/auth.ts`), so a translator reads the
 * same words the current app gives them.
 *
 * **(C) exists because of how Hexclave treats unverified users, which is not obvious.** When the
 * project requires email verification, an unverified user is *signed in* but **restricted**, and
 * their access token carries a different `aud` and `iss` (`<project-id>:restricted`,
 * `…/projects-restricted-users/<id>`). Our verifier pins both, so the token is refused and `/me`
 * answers 401 — correct, but indistinguishable at the API from a broken session. The SDK, however,
 * tells us plainly via `primaryEmailVerified`, so the *frontend* is the right place to explain it.
 * Checked **before** (B): telling someone to wait for an admin when what they actually need is to
 * click a link in their inbox is a wait that never ends.
 *
 * This is the behaviour change signup causes: registration used to issue no token at all, so the
 * old form simply stayed put with a toast. Hexclave signs the user in *during* signup, so the
 * session now exists before the approval does (plan §9 change 5).
 *
 * ═══ ONE LIMITATION, STATED RATHER THAN HIDDEN ═════════════════════════════════════════════════
 * State (B) is inferred from "Hexclave session exists, D1 row does not". It cannot distinguish an
 * unapproved account from **the API being unreachable**, because `authApi.getCurrentUser` returns
 * `null` for a 403, a 500 and a dead network alike (`src/lib/api.ts:272-289`) — the reproduced
 * DF-22 rank 5 behaviour, which `CLAUDE.md` §3 freezes. So during a Worker outage a signed-in user
 * is told they are awaiting approval rather than that the server is down.
 *
 * Distinguishing them means making `getCurrentUser` surface the status code, which changes a
 * defect this port is required to reproduce. **Not authorised here.** If the owner later approves
 * DF-22 rank 5, this screen is the place that benefits, and the fix is to branch on the real
 * `403 {"is_approved": false}` body that plan §9 change 5 names.
 */
import { useState, type ReactNode } from 'react'
import { useUser } from '@hexclave/react'
import { hexclaveApp } from '@/lib/hexclave'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

export const HexclaveAuthForm = () => {
  // `or: 'return-null'` for the same reason `HexclaveAuthProvider` uses it: this component is
  // rendered *in place* by `ProtectedRoute` and must never navigate. A redirecting variant here
  // would change the URL out from under a gate whose whole contract is that it does not.
  const hexclaveUser = useUser({ or: 'return-null' })

  // Each redirect leaves the page, so the flag never has to be cleared — it exists only to stop a
  // second click starting a second navigation before the first one lands.
  const [isRedirecting, setIsRedirecting] = useState(false)

  const go = (redirect: () => Promise<void>) => () => {
    setIsRedirecting(true)
    void redirect()
  }

  // ── (C) Signed in to Hexclave, but the email is not verified ──────────────────────────────────
  // Checked before (B) deliberately: an unverified user may have no D1 row at all (their token is
  // refused before it ever reaches the database), and "waiting for an admin" would be a wait that
  // never ends. `sendVerificationEmail` is not offered here — the hosted pages own that flow, and
  // re-sending is one of the things they already do.
  if (hexclaveUser && !hexclaveUser.primaryEmailVerified) {
    return (
      <Shell description="Confirm your email address to continue">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            We sent a verification link to{' '}
            <span className="font-medium">{hexclaveUser.primaryEmail ?? 'your email address'}</span>.
            Open it, then reload this page.
          </p>
          <p className="text-sm text-muted-foreground">
            Once your address is confirmed, an administrator still needs to approve your account
            before you can open any documents.
          </p>
          <Button
            variant="outline"
            className="w-full"
            disabled={isRedirecting}
            onClick={go(() => hexclaveApp().redirectToSignOut())}
          >
            {isRedirecting ? 'Signing out...' : 'Sign Out'}
          </Button>
        </div>
      </Shell>
    )
  }

  // ── (B) Signed in and verified, but no approved D1 row ────────────────────────────────────────
  if (hexclaveUser) {
    return (
      <Shell description="Your account is waiting to be approved">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {/* The Worker's own string, so the wording a translator sees does not change. */}
            Account not approved yet. Please contact the administrator.
          </p>
          {hexclaveUser.primaryEmail !== null && (
            // Which account is waiting. Worth showing: somebody who signed up with the wrong
            // address would otherwise have no way to tell why approval never arrives.
            <p className="text-sm text-muted-foreground">
              Signed in as <span className="font-medium">{hexclaveUser.primaryEmail}</span>.
            </p>
          )}
          <Button
            variant="outline"
            className="w-full"
            disabled={isRedirecting}
            onClick={go(() => hexclaveApp().redirectToSignOut())}
          >
            {isRedirecting ? 'Signing out...' : 'Sign Out'}
          </Button>
        </div>
      </Shell>
    )
  }

  // ── (A) No Hexclave session ───────────────────────────────────────────────────────────────────
  return (
    <Shell description="Sign in to save your documents and preambles">
      <div className="space-y-4">
        <Button
          className="w-full"
          disabled={isRedirecting}
          onClick={go(() => hexclaveApp().redirectToSignIn())}
        >
          {isRedirecting ? 'Signing in...' : 'Sign In'}
        </Button>
        <Button
          variant="outline"
          className="w-full"
          disabled={isRedirecting}
          onClick={go(() => hexclaveApp().redirectToSignUp())}
        >
          Create Account
        </Button>
        <p className="text-sm text-muted-foreground text-center">
          {/* Password reset lives on the hosted sign-in page, so it is not a separate button here.
              Naming it is still worth the line: the app has never had one, and the people using it
              have years of habit saying it does not exist. */}
          Forgotten your password? Reset it from the sign-in page.
        </p>
      </div>
    </Shell>
  )
}

/**
 * The card both states sit in, matching `LegacyAuthForm`'s wrapper exactly — same `bg-black` page,
 * same `max-w-md shadow-xl` card, same title. Switching auth mode should not look like switching
 * application, and the black wrapper is the source's literal (`AuthForm.tsx:59`), not a theme token.
 */
const Shell = ({
  description,
  children,
}: {
  description: string
  children: ReactNode
}) => (
  <div className="min-h-screen flex items-center justify-center bg-black p-4">
    <Card className="w-full max-w-md shadow-xl">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center">LaTeX to HTML Converter</CardTitle>
        <CardDescription className="text-center">{description}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  </div>
)
